import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../Shared/API/config';

export interface StagedCollectionGroup {
  collectedBy: {
    id: number | string;
    displayName: string;
  };
  paymentDate: string;
  totalAmount: number;
  paymentCount: number;
  studentCount?: number;
  status: 'pending' | 'approved' | 'rejected';
}

export interface StagedCollectionDetail {
  id: string | number;
  student: {
    id: number | string;
    displayName: string;
  };
  studentName?: string;
  category: 'subscription' | 'fees';
  amount: number;
  paymentDate: string;
  status: 'pending' | 'approved' | 'rejected';
}

export interface StudentSummarySubscription {
  name: string;
  balance: string | number;
}

export interface StudentSummary {
  totalOutstanding: number;
  totalFees: number;
  totalSubscriptions: number;
  subscriptions: StudentSummarySubscription[];
}

export interface StagedPaymentDuplicate {
  id: string | number;
  amount: string | number;
  category?: 'subscription' | 'fees';
  paymentDate?: string;
  recordedAt?: string;
  status?: string;
}

export interface CollectionRecord {
  studentId: string | number;
  amount: number;
  category: 'subscription' | 'fees';
  paymentDate?: string;
  // Identifies one recording attempt. Every retry of that attempt -- manual or
  // otherwise -- must carry the same value, so the server can return the entry
  // it already staged instead of staging the cash twice.
  idempotencyKey: string;
  // Set only after the collector has confirmed an entry the server flagged as
  // a possible duplicate is genuinely a second payment.
  confirmDuplicate?: boolean;
}

export const stagedPaymentsApi = createApi({
  reducerPath: 'stagedPaymentsApi',
  baseQuery,
  tagTypes: ['StagedPayments', 'StudentSummary'],
  endpoints: builder => ({
    getStudentSummary: builder.query<StudentSummary, string>({
      query: (studentId) => `/staged-payments/student-summary/${studentId}/`,
      transformResponse: (response: any): StudentSummary => {
        const data = response?.data || response;
        return {
          totalOutstanding: Number(data?.total || data?.outstandingBalance || data?.totalOutstanding || 0),
          totalFees: Number(data?.fees || data?.totalFees || 0),
          totalSubscriptions: Number(data?.totalSubscriptions || data?.total_subscriptions || 0),
          subscriptions: data?.subscriptions || []
        };
      },
      providesTags: ['StudentSummary'],
    }),
    recordPayment: builder.mutation<any, CollectionRecord>({
      query: (payment) => ({
        url: '/staged-payments/record/',
        method: 'POST',
        body: payment,
      }),
      invalidatesTags: ['StagedPayments', 'StudentSummary'],
    }),
    getCollections: builder.query<StagedCollectionGroup[], { status?: string; collectedBy?: string; date?: string; startDate?: string; endDate?: string } | void>({
      query: (params) => ({
        url: '/staged-payments/groups/',
        params: params || {},
      }),
      transformResponse: (response: any): StagedCollectionGroup[] => {
        const data = response?.data || response || [];
        return data.map((item: any) => ({
          ...item,
          collectedBy: item.collectedBy || item.collected_by,
          paymentDate: item.paymentDate || item.payment_date,
          totalAmount: Number(item.totalAmount || item.total_amount || 0),
          studentCount: item.studentCount || item.student_count,
          paymentCount: item.paymentCount || item.payment_count,
          status: item.status?.toLowerCase()
        }));
      },
      providesTags: ['StagedPayments'],
    }),
    getCollectionDetail: builder.query<StagedCollectionDetail[], { collectorId: string | number; paymentDate: string }>({
      query: ({ collectorId, paymentDate }) => `/staged-payments/${collectorId}/${paymentDate}/`,
      transformResponse: (response: any): StagedCollectionDetail[] => {
        const data = response?.data || response || [];
        return data.map((item: any) => ({
          ...item,
          paymentDate: item.paymentDate || item.payment_date,
          collectedBy: item.collectedBy || item.collected_by,
          amount: Number(item.amount || 0)
        }));
      },
      providesTags: ['StagedPayments'],
    }),
    updateCollectionRecord: builder.mutation<any, { id: string | number; amount?: number; category?: 'subscription' | 'fees' }>({
      query: ({ id, ...body }) => ({
        url: `/staged-payments/record/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['StagedPayments', 'StudentSummary'],
    }),
    deleteCollectionRecord: builder.mutation<any, string | number>({
      query: (id) => ({
        url: `/staged-payments/record/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['StagedPayments', 'StudentSummary'],
    }),
    approveCollection: builder.mutation<any, { collectedBy: string; paymentDate: string }>({
      query: (data) => ({
        url: '/staged-payments/approve-group/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['StagedPayments'],
    }),
    rejectCollection: builder.mutation<any, { collectedBy: string; paymentDate: string; reason: string }>({
      query: (data) => ({
        url: '/staged-payments/reject-group/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['StagedPayments'],
    }),
  }),
});

export const {
  useGetStudentSummaryQuery,
  useRecordPaymentMutation,
  useGetCollectionsQuery,
  useGetCollectionDetailQuery,
  useUpdateCollectionRecordMutation,
  useDeleteCollectionRecordMutation,
  useApproveCollectionMutation,
  useRejectCollectionMutation,
} = stagedPaymentsApi;
